import { useMemo, useState, useEffect, useContext } from 'react';
import {
  Icon,
  Link,
  Utils,
  FlexLayout,
  ButtonColors,
  StandardButton,
} from '@gv/triage-components';

import { TranscriptViewer } from 'components/transcript-viewer';
import { AudioPlayer, AudioContext } from 'components/audio-player';
import * as CommonStyles from 'components/communication-log/styles';

import { SelectedEventPageProps } from '../types';
import { CallTranscription } from '../../../../store/api/hospitals/types';

import * as Styles from './styles';

const CallEventContent = ({
  event,
  currentCase,
  recordingUrl,
}: {
  event: any;
  currentCase: any;
  recordingUrl: string;
}) => {
  const { ref: audioRef } = useContext(AudioContext);
  const [audioTime, setAudioTime] = useState(0);

  const transcription = useMemo(() => {
    if (!currentCase?.transcriptions || !event.callId) {
      return null;
    }
    return (
      currentCase.transcriptions.find(
        (t: CallTranscription) => t.outgoing_id === event.callId
      ) || null
    );
  }, [currentCase?.transcriptions, event.callId]);

  useEffect(() => {
    const audio = audioRef?.current;
    if (!audio) {
      return;
    }

    const handleTimeUpdate = () => {
      setAudioTime(audio.currentTime);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    return () => audio.removeEventListener('timeupdate', handleTimeUpdate);
  }, [audioRef]);

  const handleTimestampClick = (time: number) => {
    if (audioRef?.current) {
      audioRef.current.currentTime = time;
      audioRef.current.play();
    }
  };

  return (
    <>
      <AudioPlayer.PlayerContainer>
        <AudioPlayer.Player />
      </AudioPlayer.PlayerContainer>

      {recordingUrl && (
        <div style={{ height: '500px' }}>
          {transcription ? (
            <TranscriptViewer
              error={null}
              isLoading={false}
              currentTime={audioTime}
              transcription={transcription}
              onTimestampClick={handleTimestampClick}
            />
          ) : (
            <div
              style={{ color: '#666', padding: '20px', textAlign: 'center' }}
            >
              No Transcript Available
            </div>
          )}
        </div>
      )}
    </>
  );
};

export const CallEvent = ({
  event,
  onClose,
  currentCase,
  relatedEvents,
  onSelectEvent,
}: SelectedEventPageProps): JSX.Element => {
  const recordingUrl =
    event.details && 'recording' in event.details && event.details.recording;

  return (
    <>
      <CommonStyles.Header justifyContent="space-between">
        <FlexLayout gap={8}>
          <StandardButton
            padding="0"
            onClick={onClose}
            icon={<Icon.ChevronLeft />}
            colorType={ButtonColors.Transparent}
          />

          <div>
            <h3>Call {event.callId}</h3>
            <p className="caption-regular">
              {Utils.Date.formatDate(event.timestamp, 'MMMM D, YYYY LT')}
            </p>
          </div>
        </FlexLayout>

        {recordingUrl && (
          <Link
            download
            withoutButton
            text="Download"
            url={recordingUrl}
            className="semibold"
          />
        )}
      </CommonStyles.Header>

      <CommonStyles.EventContent>
        <FlexLayout gap={24} flexDirection="column">
          {recordingUrl && (
            <AudioPlayer src={recordingUrl}>
              <CallEventContent
                event={event}
                currentCase={currentCase}
                recordingUrl={recordingUrl}
              />
            </AudioPlayer>
          )}

          {relatedEvents && relatedEvents?.length > 0 && (
            <Styles.Events events={relatedEvents} onClick={onSelectEvent} />
          )}
        </FlexLayout>
      </CommonStyles.EventContent>
    </>
  );
};
